'use client'

import Image from 'next/image'
import { useState } from 'react'

import { iconFor } from '@/lib/rental-icons'
import { PRODUCT_STAGE, RADIUS, SURFACE_SHEET } from '@/lib/surface'
import { META } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * The item's photograph, big.
 *
 * A client island for one reason: `onError`. The blob URL comes from the
 * database and can outlive the file behind it, and a broken `<Image>` paints
 * its alt text across the frame — which reads as a broken page rather than as a
 * missing picture. The labelled frame below is what the listing cards already
 * fall back to.
 *
 * `object-contain`, not `cover`: these are cut-out product shots on a flat
 * backdrop, and cropping a lens to fill a box removes the end of the lens.
 *
 * No intrinsic dimensions are recorded at upload (`asset.width`/`height` are
 * never populated), so `fill` plus an explicit `sizes` is the only correct
 * form here.
 */
export function RentalPhoto({
  src,
  alt,
  name,
  categorySlug,
}: {
  src: string | null
  alt: string | null
  name: string
  categorySlug: string
}) {
  const [failed, setFailed] = useState(false)
  const Icon = iconFor(categorySlug)

  return (
    /* The same object as a grid card, larger: a white sheet with the black
       picture window inside it. The window alone would not have worked here —
       the stage is the same black as the page, and on a card that is fine
       because the sheet around it draws the boundary, while on this page there
       is no sheet and the panel would simply have no edges. So the sheet comes
       with it, as a mat around the picture. The alternative, a hairline on a
       black panel, frames the black rather than the photograph.

       A lens has to look the same in the list and on its own page; two
       different backgrounds for one product is how a catalogue stops looking
       like a catalogue. */
    <div className={cn('p-2', RADIUS.feature, SURFACE_SHEET)}>
      <div
        className={cn(
          'relative aspect-[4/3] w-full overflow-hidden sm:aspect-[16/10]',
          // One step tighter than the sheet's radius, so the two curves are
          // concentric rather than parallel — an inner corner at the same
          // radius as its outer reads as a mistake at this padding.
          RADIUS.box,
        )}
        style={{ background: PRODUCT_STAGE }}
      >
        {src && !failed ? (
          <Image
            src={src}
            alt={alt ?? name}
            fill
            priority
            sizes="(max-width: 1023px) 100vw, 55vw"
            onError={() => setFailed(true)}
            className="object-contain p-8 sm:p-12"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/55">
            <Icon size={40} aria-hidden="true" />
            <span className={cn(META, 'px-6 text-center font-medium')}>{name}</span>
          </div>
        )}
      </div>
    </div>
  )
}
